import { NgClass, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { MatIcon } from '@angular/material/icon';

export enum WarningMessageType {
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
  Success = 'success',
}

@Component({
  selector: 'shared-warning-message',
  standalone: true,
  imports: [MatIcon, NgOptimizedImage, NgClass],
  templateUrl: './warning-message.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarningMessageComponent {
  protected readonly types = WarningMessageType;

  text = input.required<string>();
  subText = input<string>();
  type = input<WarningMessageType>(WarningMessageType.Warning);
  size = input<'big' | 'small'>('big');
  withBorder = input<boolean>(false);
  /** Optional Tailwind text-size class (e.g. `text-xs`, `text-sm`, `text-lg`) applied to the message; empty keeps the size-derived default. */
  textSize = input<string>('');
  /** Optional Tailwind padding class (e.g. `p-2`, `px-3 py-1`) overriding the size-derived padding; empty keeps the default. */
  padding = input<string>('');

  /**
   * Root container classes. The size-derived text-size / padding defaults are dropped whenever a
   * `textSize` / `padding` override is supplied, so the override wins without needing `!important`.
   */
  protected readonly rootClass = computed(() => {
    const big = this.size() === 'big';
    const small = this.size() === 'small';
    const type = this.type();
    const textSize = this.textSize();
    const padding = this.padding();

    return {
      border: this.withBorder() && small,
      'border-2': this.withBorder() && big,
      'border-error-500 bg-warning-50': type === WarningMessageType.Error,
      'border-warning-500 bg-warning-100': type === WarningMessageType.Warning,
      'border-primary-500 bg-info-100': type === WarningMessageType.Info,
      'border-success-500 bg-success-100': type === WarningMessageType.Success,
      'w-full gap-4 rounded-xl font-semibold': big,
      'w-fit gap-0.5 rounded-lg font-light': small,
      'p-8': big && !padding,
      'px-2 py-0': small && !padding,
      'text-sm': big && !textSize,
      'text-xs': small && !textSize,
      [textSize]: !!textSize,
      [padding]: !!padding,
    };
  });
}
